import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, Send, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { helpApi } from "../../lib/api";
import { useHelpChat } from "../../contexts/HelpChatContext";

const WELCOME = {
  role: "assistant",
  text: "Hi! I'm your Help & Support assistant. Ask me anything about jobs, estimates, invoices, customers, AI agents, staff, billing, or settings.",
};

const STORAGE_KEY = "helpChatWidgetState";

const EMAIL_URL_RE = /(https?:\/\/[^\s]+|[\w.+-]+@[\w-]+\.[\w.-]+)/g;

function linkify(text) {
  const parts = String(text || "").split(EMAIL_URL_RE);
  return parts.map((part, i) => {
    if (!part) return null;
    if (/^https?:\/\//.test(part)) {
      return (
        <a key={i} href={part} target="_blank" rel="noreferrer" className="underline text-blue-300 hover:text-blue-200">
          {part}
        </a>
      );
    }
    if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(part)) {
      return (
        <a key={i} href={`mailto:${part}`} className="underline text-blue-300 hover:text-blue-200">
          {part}
        </a>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function loadPersisted() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function HelpChatWidget() {
  const { open, setOpen } = useHelpChat();
  const persisted = loadPersisted();
  const [messages, setMessages] = useState(persisted?.messages || [WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(persisted?.sessionId || null);
  const [pos, setPos] = useState(persisted?.pos || null); // null = default bottom-right position
  const scrollRef = useRef(null);
  const panelRef = useRef(null);
  const dragRef = useRef(null);

  // Persist conversation + position across route changes / full page reloads within the same tab.
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ messages, sessionId, pos }));
    } catch {
      // best-effort — ignore storage errors (e.g. private mode quota)
    }
  }, [messages, sessionId, pos]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, sending]);

  const moveTo = useCallback((clientX, clientY) => {
    const drag = dragRef.current;
    const panel = panelRef.current;
    if (!drag || !panel) return;
    const rect = panel.getBoundingClientRect();
    const dx = clientX - drag.startX;
    const dy = clientY - drag.startY;
    const maxLeft = Math.max(window.innerWidth - rect.width - 8, 8);
    const maxTop = Math.max(window.innerHeight - rect.height - 8, 8);
    const left = Math.min(Math.max(drag.origLeft + dx, 8), maxLeft);
    const top = Math.min(Math.max(drag.origTop + dy, 8), maxTop);
    setPos({ left, top });
  }, []);

  const onMouseMove = useCallback((e) => moveTo(e.clientX, e.clientY), [moveTo]);
  const onTouchMove = useCallback((e) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const t = e.touches[0];
    moveTo(t.clientX, t.clientY);
  }, [moveTo]);

  const endDrag = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", endDrag);
    window.removeEventListener("touchmove", onTouchMove);
    window.removeEventListener("touchend", endDrag);
  }, [onMouseMove, onTouchMove]); // eslint-disable-line react-hooks/exhaustive-deps

  const startDrag = (clientX, clientY) => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragRef.current = { startX: clientX, startY: clientY, origLeft: rect.left, origTop: rect.top };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", endDrag);
  };

  const onHeaderMouseDown = (e) => {
    if (e.target.closest("button")) return; // don't drag when clicking the close button
    startDrag(e.clientX, e.clientY);
  };
  const onHeaderTouchStart = (e) => {
    if (e.target.closest("button")) return;
    const t = e.touches[0];
    startDrag(t.clientX, t.clientY);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", endDrag);
    };
  }, [onMouseMove, onTouchMove, endDrag]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setSending(true);
    try {
      const res = await helpApi.chat({ sessionId, message: text });
      setSessionId(res.sessionId);
      setMessages((prev) => [...prev, { role: "assistant", text: res.reply, sources: res.sources }]);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) {
        setMessages((prev) => [...prev, { role: "assistant", text: detail || "You're out of AI credits. Purchase more to keep using the Help chatbot." }]);
      } else {
        toast.error("Help chatbot failed to respond.");
        setMessages((prev) => [...prev, { role: "assistant", text: "Sorry, something went wrong answering that. Please try again." }]);
      }
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!open) return null;

  const panelStyle = pos
    ? { left: pos.left, top: pos.top, right: "auto", bottom: "auto" }
    : undefined;

  return (
    <div
      ref={panelRef}
      data-testid="help-chat-panel"
      style={panelStyle}
      className={`fixed z-[60] w-[380px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-8rem)] flex flex-col rounded-2xl border border-white/10 bg-[#0B1E3F] shadow-2xl shadow-black/40 overflow-hidden animate-in fade-in slide-in-from-bottom-3 duration-200 ${
        pos ? "" : "bottom-24 right-6"
      }`}
    >
      <div
        onMouseDown={onHeaderMouseDown}
        onTouchStart={onHeaderTouchStart}
        className="flex items-center justify-between gap-2 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-blue-600/20 to-transparent cursor-move select-none"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4 text-blue-400" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-100 truncate">Help &amp; Support</div>
            <div className="text-[11px] text-slate-400 truncate">Ask about any feature</div>
          </div>
        </div>
        <button
          data-testid="help-chat-close-button"
          onClick={() => setOpen(false)}
          className="h-8 w-8 flex items-center justify-center bg-transparent hover:opacity-70 transition-opacity shrink-0"
        >
          <X className="h-4 w-4" style={{ color: "#ffffff" }} />
        </button>
      </div>

      <div ref={scrollRef} data-testid="help-chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-blue-600 text-white rounded-br-sm"
                  : "bg-white/[0.06] text-slate-200 border border-white/10 rounded-bl-sm"
              }`}
            >
              {linkify(m.text)}
              {m.sources && m.sources.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/10 flex flex-wrap gap-1.5">
                  {m.sources.map((s) => (
                    <span
                      key={s.id}
                      data-testid="help-chat-source-pill"
                      className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/20"
                    >
                      {s.title}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm px-3.5 py-2.5 bg-white/[0.06] border border-white/10">
              <Loader2 className="h-3.5 w-3.5 text-slate-400 animate-spin" />
            </div>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-white/10 flex items-end gap-2">
        <textarea
          data-testid="help-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask a question..."
          rows={1}
          className="flex-1 resize-none rounded-xl bg-white/[0.06] border border-white/10 px-3 py-2.5 text-[13px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 max-h-24"
        />
        <button
          data-testid="help-chat-send-button"
          onClick={send}
          disabled={sending || !input.trim()}
          className="h-10 w-10 shrink-0 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:hover:bg-blue-600 text-white flex items-center justify-center transition-colors active:scale-95"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
